import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  BellRing, CheckCircle2, Clock, FileText, Lock, Loader2,
  ShieldCheck, Store, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { useLaunchpadNotify } from "@/hooks/useLaunchpad";
import { cn } from "@/lib/utils";

/* ═══════════════ Notify me ═══════════════ */

export function NotesNotifyMe({ source, className }: { source: string; className?: string }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const notify = useLaunchpadNotify();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(value)) {
      toast.error("Enter a valid email address");
      return;
    }
    try {
      await notify.mutateAsync({ email: value, source });
      setDone(true);
      toast.success("You're on the list — we'll email you when the Notes Market opens.");
    } catch {
      toast.error("Could not add you to the list. Please try again.");
    }
  };

  if (done) {
    return (
      <div className={cn("flex items-center justify-center gap-2 text-sm text-primary", className)}>
        <CheckCircle2 className="h-4 w-4" />
        <span>You're on the list — we'll notify you at launch.</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={cn("flex w-full max-w-md flex-col gap-2 sm:flex-row", className)}>
      <Input
        type="email"
        inputMode="email"
        autoComplete="email"
        aria-label="Email address"
        placeholder="you@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="h-11"
      />
      <Button type="submit" className="h-11 shrink-0 gap-2" disabled={notify.isPending}>
        {notify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
        Notify me
      </Button>
    </form>
  );
}

/* ═══════════════ Locked action ═══════════════ */

export function NotesLockedAction({ label = "Buy note", full }: { label?: string; full?: boolean }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("inline-flex", full && "w-full")}>
            <Button variant="secondary" disabled className={cn("gap-2", full && "w-full")}>
              <Lock className="h-3.5 w-3.5" />
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>Launching soon</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ═══════════════ What launches first ═══════════════ */

const LAUNCH_STEPS = [
  { icon: ShieldCheck, title: "Verified notes only", desc: "Every note is checked and locked to its owner before it can be listed." },
  { icon: Store, title: "Open listings", desc: "Owners set an asking price; buyers browse and buy at the agreed price." },
  { icon: TrendingUp, title: "Hold or resell", desc: "Hold to maturity for the full payout, or resell on the secondary market." },
];

export function NotesLaunchStrip() {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {LAUNCH_STEPS.map((s, i) => (
        <div key={s.title} className="rounded-xl border border-border bg-card/50 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <s.icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                <span className="text-muted-foreground">{i + 1}. </span>
                {s.title}
              </p>
              <p className="text-xs text-muted-foreground">At launch</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{s.desc}</p>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════ Locked listing placeholders ═══════════════ */

const PLACEHOLDERS = [
  { name: "Note Slot One" },
  { name: "Note Slot Two" },
  { name: "Note Slot Three" },
];

function LockedNoteCard({ name }: { name: string }) {
  return (
    <Card className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 z-10 bg-background/45 backdrop-blur-[1px]" />
      <div className="pointer-events-none absolute right-3 top-3 z-20">
        <Badge variant="secondary" className="gap-1">
          <Lock className="h-3 w-3" /> Coming Soon
        </Badge>
      </div>
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold">{name}</p>
            <p className="text-xs text-muted-foreground">Reference TBA</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Face value</p>
            <p className="font-semibold tabular-nums">TBA</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Asking price</p>
            <p className="font-semibold tabular-nums">TBA</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>Listing opens at launch</span>
        </div>
        <NotesLockedAction full />
      </CardContent>
    </Card>
  );
}

/* ═══════════════ Main surface ═══════════════ */

export interface NotesMarketComingSoonProps {
  /** Where the signup came from — used to segment the notify list. */
  source?: string;
  /** Public marketing page adds its own outer padding. */
  variant?: "public" | "app";
  className?: string;
}

export default function NotesMarketComingSoon({
  source = "app_notes_market",
  variant = "app",
  className,
}: NotesMarketComingSoonProps) {
  return (
    <div className={cn("space-y-8", variant === "public" && "mx-auto max-w-6xl px-6 py-16", className)}>
      <section className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-background p-6 text-center sm:p-10">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-5">
          <Badge variant="secondary" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Notes Market
          </Badge>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Notes Market is coming soon</h1>
          <p className="text-sm text-muted-foreground sm:text-base">
            A secondary market where verified notes can be bought and sold before maturity. Buying, listing and
            subscriptions are locked until launch — add your email and we'll tell you the moment it opens.
          </p>
          <NotesNotifyMe source={source} className="justify-center" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            <span>No trading, no fees and no listings until the market is live.</span>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">What launches first</h2>
        <NotesLaunchStrip />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Listings preview</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PLACEHOLDERS.map((p) => (
            <LockedNoteCard key={p.name} name={p.name} />
          ))}
        </div>
      </section>
    </div>
  );
}
